import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ApexColorScheme = 'light' | 'dark' | 'system';
export type ApexEffectiveColorScheme = 'light' | 'dark';

export const APEX_THEME_STORAGE_KEYS = {
  public: 'apexPublicColorScheme',
  backoffice: 'apexBackofficeColorScheme',
  owner: 'apexOwnerColorScheme',
  tenant: 'apexTenantColorScheme',
  maintenance: 'apexMaintenanceColorScheme',
} as const;

export type ApexThemeApp = keyof typeof APEX_THEME_STORAGE_KEYS;

export const APEX_THEME_STORAGE_KEY = new InjectionToken<string>('APEX_THEME_STORAGE_KEY', {
  providedIn: 'root',
  factory: () => APEX_THEME_STORAGE_KEYS.public,
});

export function isApexColorScheme(value: unknown): value is ApexColorScheme {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function resolveEffectiveApexTheme(
  preference: ApexColorScheme,
  mediaQuery?: MediaQueryList | null,
): ApexEffectiveColorScheme {
  if (preference !== 'system') {
    return preference;
  }

  if (mediaQuery) {
    return mediaQuery.matches ? 'dark' : 'light';
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

export function applyApexThemeToDocument(
  document: Document,
  preference: ApexColorScheme,
  mediaQuery?: MediaQueryList | null,
): ApexEffectiveColorScheme {
  const effectiveTheme = resolveEffectiveApexTheme(preference, mediaQuery);
  const root = document.documentElement;
  if (!root) {
    return effectiveTheme;
  }

  root.setAttribute('data-theme', effectiveTheme);
  root.setAttribute('data-theme-preference', preference);
  root.classList.toggle('theme-dark', effectiveTheme === 'dark');
  root.classList.toggle('theme-light', effectiveTheme === 'light');
  root.classList.toggle('dark', effectiveTheme === 'dark');
  root.style.colorScheme = effectiveTheme;

  if (document.body) {
    document.body.setAttribute('data-theme', effectiveTheme);
    document.body.setAttribute('data-theme-preference', preference);
    document.body.classList.toggle('dark', effectiveTheme === 'dark');
  }

  return effectiveTheme;
}

@Injectable({
  providedIn: 'root',
})
export class ApexThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = inject(APEX_THEME_STORAGE_KEY);
  private readonly mediaQuery = this.createMediaQuery();
  private readonly preferenceSubject: BehaviorSubject<ApexColorScheme>;

  readonly preference$: Observable<ApexColorScheme>;

  constructor() {
    this.preferenceSubject = new BehaviorSubject<ApexColorScheme>(this.readInitialPreference());
    this.preference$ = this.preferenceSubject.asObservable();
    this.applyCurrentPreference();

    this.mediaQuery?.addEventListener?.('change', () => {
      if (this.currentPreference === 'system') {
        this.applyCurrentPreference();
      }
    });
  }

  get currentPreference(): ApexColorScheme {
    return this.preferenceSubject.value;
  }

  get effectiveColorScheme(): ApexEffectiveColorScheme {
    return resolveEffectiveApexTheme(this.currentPreference, this.mediaQuery);
  }

  get hasStoredPreference(): boolean {
    return this.readStoredPreference() !== null;
  }

  setPreference(preference: ApexColorScheme): void {
    this.preferenceSubject.next(preference);
    this.applyCurrentPreference();

    if (isPlatformBrowser(this.platformId)) {
      try {
        window.localStorage.setItem(this.storageKey, preference);
      } catch {
        // Storage is unavailable in some privacy modes; the active DOM theme still updates.
      }
    }
  }

  toggleLightDark(): void {
    this.setPreference(this.effectiveColorScheme === 'dark' ? 'light' : 'dark');
  }

  private applyCurrentPreference(): void {
    applyApexThemeToDocument(this.document, this.currentPreference, this.mediaQuery);
  }

  private readInitialPreference(): ApexColorScheme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'system';
    }

    return this.readStoredPreference() ?? 'system';
  }

  private readStoredPreference(): ApexColorScheme | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = window.localStorage.getItem(this.storageKey);
      return isApexColorScheme(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private createMediaQuery(): MediaQueryList | null {
    if (!isPlatformBrowser(this.platformId) || typeof window.matchMedia !== 'function') {
      return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
  }
}
